import { Injectable, signal, computed, inject } from '@angular/core';
import { MSAL_INSTANCE } from '@azure/msal-angular';
import type { IPublicClientApplication } from '@azure/msal-browser';
import { AppUser, Role } from '../models';
import { WalletService } from './wallet.service';

/** Derives initials from an email's local-part (e.g. "ah.abdellatif96" -> "AA"); there is no
 *  backend "display name" field surfaced anywhere, so the email is the only real identity fact. */
function initials(email: string): string {
  const local = email.split('@')[0] || '';
  const parts = local.split(/[._-]+/).filter(Boolean);
  const chars = parts.length >= 2 ? [parts[0][0], parts[1][0]] : [local.slice(0, 2)];
  return chars.join('').toUpperCase().slice(0, 2) || '??';
}

/** Identity is backed by the MSAL (Azure AD B2C) active account. MSAL is initialized and the
 *  B2C redirect is processed by the APP_INITIALIZER (app.config.ts) BEFORE this service is first
 *  read, and login()/logout() are full-page redirects — so reading the active account in the
 *  field initializer is always current. In dev the BE still receives environment.devAgencyToken
 *  via devAuthInterceptor (skipAuth=true), so the local BE contract is unchanged.
 *  "View as" role override (prototype parity) still persists to localStorage. */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private wallet = inject(WalletService);
  // msal-angular mistypes MSAL_INSTANCE as InjectionToken<string>; the value is the client app.
  private msal = inject(MSAL_INSTANCE) as unknown as IPublicClientApplication;

  private readonly _account = signal(this.msal.getActiveAccount());
  private readonly _role = signal<Role>((localStorage.getItem('hbh_role') as Role) || 'AGENCY_MANAGER');

  readonly authed = computed<boolean>(() => this._account() !== null);
  readonly role = this._role.asReadonly();
  readonly isRoot = computed(() => this._role() === 'AGENCY_MANAGER');

  /** Real `emails` claim from the B2C id token (falls back to `email` / username). */
  private readonly email = computed<string>(() => {
    const claims = this._account()?.idTokenClaims as Record<string, unknown> | undefined;
    const emails = claims?.['emails'] as string[] | undefined;
    return emails?.[0]
      ?? (claims?.['email'] as string | undefined)
      ?? this._account()?.username
      ?? '';
  });

  readonly user = computed<AppUser>(() => {
    const email = this.email();
    return {
      name: email || 'Unknown user',
      email,
      role: this._role(),
      agency: this.wallet.agencyName() || '—',
      initials: email ? initials(email) : '—',
    };
  });

  /** Redirects to the Azure AD B2C sign-in page; returns to redirectUri once authenticated. */
  login(): void { void this.msal.loginRedirect({ scopes: ['openid', 'offline_access'] }); }
  logout(): void { void this.msal.logoutRedirect(); }
  setRole(r: Role): void { this._role.set(r); localStorage.setItem('hbh_role', r); }
}
